import { useEffect, useState } from "react";
import { useAuth } from "../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../config/api";
import { formatUsd } from "../../../../components/usageBilling/usageFormat";

// Answer verbosity — bound to the session per Ref/frontend_change_requests_en.md
// item 2; changing it only takes effect on the next New Chat.
export const VERBOSITY_OPTIONS = [
  {
    id: "low",
    label: { en: "Short", zh: "簡短" },
    description: {
      en: "Quick conclusion, fewer details",
      zh: "快速給結論，省略細節",
    },
  },
  {
    id: "medium",
    label: { en: "Balanced", zh: "適中" },
    description: {
      en: "Default — balances speed and completeness",
      zh: "預設，平衡速度與完整度",
    },
  },
  {
    id: "high",
    label: { en: "Detailed", zh: "詳盡" },
    description: {
      en: "Full explanation, longer wait",
      zh: "完整說明，較長等待時間",
    },
  },
];

// Thinking depth — separate axis from verbosity (Ref/04_frontend_changes.md
// item 3): verbosity controls answer length, thinking controls how hard the
// model reasons before writing. All current grounding models support all
// 3 levels (backend maps/clamps per-model constraints), so no per-model
// filtering needed here. Bound to the session like verbosity/grounding_model.
export const THINKING_OPTIONS = [
  {
    id: "fast",
    label: { en: "Fast", zh: "快速" },
    description: {
      en: "Minimal reasoning, quickest response",
      zh: "推理最少，回應最快",
    },
  },
  {
    id: "balanced",
    label: { en: "Balanced", zh: "均衡" },
    description: {
      en: "Default — reasonable reasoning depth",
      zh: "預設，推理深度適中",
    },
  },
  {
    id: "deep",
    label: { en: "Deep", zh: "深入" },
    description: {
      en: "Most thorough reasoning, slowest",
      zh: "推理最深入，速度最慢",
    },
  },
];

// GPT exposes two tiers, not three. Underneath, `fast` and `balanced` are the
// same OpenAI reasoning mode (standard) differing only in effort, while `deep`
// switches to a different mode entirely — so presenting three ascending depths
// would describe a gradient that does not exist. "Standard" maps to `balanced`
// rather than `fast` because it is the backend's default and the safer of the
// two; `fast` stays a valid value with no UI entry point.
export const GPT_THINKING_OPTIONS = [
  {
    id: "balanced",
    label: { en: "Standard", zh: "標準" },
    description: {
      en: "Default — reliable across all models",
      zh: "預設，所有模型都穩定",
    },
  },
  {
    id: "deep",
    label: { en: "Deep", zh: "深入" },
    description: {
      en: "Most thorough, much slower and pricier",
      zh: "最完整，明顯更慢更貴",
    },
  },
];

// Grounding model — web-search path only, bound to the session per
// Ref/frontend_change_requests_en.md item 3. Curated down to these 3 from
// PIMS_FAQ's 9 tested candidates (ref/grounding_model_selection_2026_08_27.md
// §3.4) — user's call on which to surface. Model ids are Google's and may be
// renamed/deprecated on their schedule — revisit this list if that happens.
//
// speedRating/costRating are relative tiers (1-3), not exact figures — see
// PRICE_TABLE below and the "Price list" flyout for the real measured
// numbers. Derived from Ref/04_frontend_changes.md §4's 2026-08-31 measured
// table (balanced-tier median latency, token cost + grounding fee):
//   flash-lite  14.1s  ~NT$0.6   → fastest, cheapest
//   2.5-pro     27.8s  ~NT$1.8   → slowest median, priciest (also widest
//                                  variance: 22.9-49.8s on identical settings)
//   3.1-pro-preview 36.0s ~NT$1.5
//   flash       44.0s  ~NT$1.5   → slowest raw latency
// This corrects the previous version of this list, which had
// gemini-3.1-pro-preview labeled "Slowest"/"Priciest" — that was backwards;
// measurement showed gemini-2.5-pro holds both titles.
const MODEL_OPTIONS = [
  {
    id: "gemini-3.5-flash-lite",
    label: "gemini-3.5-flash-lite",
    isDefault: true,
    speedRating: 1,
    costRating: 1,
    speed: { en: "Fastest", zh: "最快" },
    depth: {
      en: "Matches flash's depth, much faster",
      zh: "接近 flash 的深度，但快很多",
    },
  },
  // gemini-3.1-pro-preview was removed on 2026-09-11: it does not respect the
  // selected content length, generating far past "medium", and proved unstable
  // in backend testing. Root Agent rejects the id outright, so offering it would
  // silently fall back to the default and look like the picker did nothing.
  // PRICE_TABLE_TWD still carries its measured rows; they simply go unused.
  {
    id: "gemini-3.5-flash",
    label: "gemini-3.5-flash",
    speedRating: 3,
    costRating: 2,
    speed: { en: "Slow", zh: "慢" },
    depth: {
      en: "Most thorough, explains source disagreement",
      zh: "最完整，會解釋來源間的分歧",
    },
  },
  {
    id: "gemini-2.5-pro",
    label: "gemini-2.5-pro",
    speedRating: 3,
    costRating: 3,
    speed: { en: "Slowest, widest variance", zh: "最慢，波動也最大" },
    depth: {
      en: "The most stable pro-tier model available today",
      zh: "目前最穩定的 pro 級模型",
    },
  },
  // GPT models answer by searching tglobalcorp.com live rather than querying our
  // product database, which is why they take 1-2 minutes and bill per search.
  //
  // Their ratings are an ordering, not a measurement: each has only 1-2 real
  // runs behind it, unlike the Gemini rows above (72 requests). The same
  // question on the same model has been seen to vary in cost by 30x depending on
  // how many searches it triggers, so do not read these as precise tiers.
  //
  // None is marked isDefault: Gemini stays the default path, and the picker uses
  // isDefault both as its fallback and to draw the "default" badge, so a second
  // one would put the badge on the wrong row.
  {
    id: "gpt-5.6-luna",
    label: "gpt-5.6-luna",
    provider: "gpt",
    // OpenAI's model page for luna documents `reasoning.effort` only, with no
    // `reasoning.mode` — it has no pro mode, which is why every `deep` run
    // against it failed in backend testing. See
    // Ref/gpt56_reasoning_modes_2026_09_13.md.
    noDeepTier: true,
    speedRating: 2,
    costRating: 1,
    speed: { en: "Moderate", zh: "中等" },
    depth: {
      en: "Cheapest option — validated for quality on the standard test question",
      zh: "成本最低——已在標準測試題驗證過品質",
    },
  },
  {
    id: "gpt-5.6-terra",
    label: "gpt-5.6-terra",
    provider: "gpt",
    speedRating: 1,
    costRating: 2,
    speed: { en: "Fastest", zh: "最快" },
    depth: {
      en: "Mid-tier cost, strong result on the standard test question",
      zh: "成本居中，標準測試題表現良好",
    },
  },
  {
    id: "gpt-5.6-sol",
    label: "gpt-5.6-sol",
    provider: "gpt",
    // Withheld rather than unsupported: sol does have a pro mode, but it is the
    // priciest model and its deep runs timed out 3/3 in backend testing, so the
    // tier stays off while cost is still being evaluated. Removing the flag is
    // all that is needed to offer it again.
    noDeepTier: true,
    speedRating: 3,
    costRating: 3,
    speed: { en: "Slowest, most variable", zh: "最慢，波動也最大" },
    depth: {
      en: "Most thorough, and the slowest and priciest of the three",
      zh: "最完整，也是三者中最慢、最貴的",
    },
  },
];

// Which models a tab may offer. The FAQ service only understands the Gemini
// ids, so it keeps the Gemini-only list; TG PIMS Agent's backend routes on the
// id and accepts both. Passing the wrong list would show models that silently
// fall back to a default, looking like the picker did nothing.
export const GROUNDING_MODEL_OPTIONS = MODEL_OPTIONS.filter(
  (o) => o.provider !== "gpt",
);
export const ALL_MODEL_OPTIONS = MODEL_OPTIONS;

// Full measured (model × thinking) table for the "Price list" flyout —
// Ref/04_frontend_changes.md §4, 72 requests against the deployed service
// (3 runs × 2 questions × 12 cells), measured 2026-08-31. tokenCost is the
// grounding + synthesis call's token spend; groundingFee is the flat
// per-search charge (free within the shared monthly quota — see the doc for
// details). Do not scale these to guess future numbers; ask PIMS_FAQ to
// re-measure instead.
export const PRICE_TABLE_TWD = [
  {
    model: "gemini-3.5-flash-lite",
    thinking: "fast",
    latency: "10.9–14.7s",
    latencyMedian: "13.6s",
    tokenCost: "NT$0.11–0.18",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-3.5-flash-lite",
    thinking: "balanced",
    latency: "11.7–18.4s",
    latencyMedian: "14.1s",
    tokenCost: "NT$0.11–0.20",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-3.5-flash-lite",
    thinking: "deep",
    latency: "19.4–31.3s",
    latencyMedian: "23.8s",
    tokenCost: "NT$0.23–0.32",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-3.5-flash",
    thinking: "fast",
    latency: "23.5–39.2s",
    latencyMedian: "28.2s",
    tokenCost: "NT$0.59–0.80",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-3.5-flash",
    thinking: "balanced",
    latency: "36.7–58.8s",
    latencyMedian: "44.0s",
    tokenCost: "NT$0.96–1.19",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-3.5-flash",
    thinking: "deep",
    latency: "37.8–61.6s",
    latencyMedian: "52.1s",
    tokenCost: "NT$0.86–1.53",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-2.5-pro",
    thinking: "fast",
    latency: "26.9–41.7s",
    latencyMedian: "39.2s",
    tokenCost: "NT$0.50–0.85",
    groundingFee: "NT$1.09",
  },
  {
    model: "gemini-2.5-pro",
    thinking: "balanced",
    latency: "22.9–49.8s",
    latencyMedian: "27.8s",
    tokenCost: "NT$0.37–1.06",
    groundingFee: "NT$1.09",
  },
  {
    model: "gemini-2.5-pro",
    thinking: "deep",
    latency: "22.5–54.2s",
    latencyMedian: "43.2s",
    tokenCost: "NT$0.44–0.90",
    groundingFee: "NT$1.09",
  },
  {
    model: "gemini-3.1-pro-preview",
    thinking: "fast",
    latency: "10.3–22.5s",
    latencyMedian: "19.9s",
    tokenCost: "NT$0.10–0.53",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-3.1-pro-preview",
    thinking: "balanced",
    latency: "29.0–40.3s",
    latencyMedian: "36.0s",
    tokenCost: "NT$0.84–1.27",
    groundingFee: "NT$0.43",
  },
  {
    model: "gemini-3.1-pro-preview",
    thinking: "deep",
    latency: "24.5–41.2s",
    latencyMedian: "33.0s",
    tokenCost: "NT$0.71–0.92",
    groundingFee: "NT$0.43",
  },
];

// Google's official per-1M-token pricing (Vertex AI, Global/Standard tier,
// ≤200K context) — from PIMS_FAQ's ref/grounding_model_cost_2026_08_27.md.
// This is the raw unit price, not what a user actually waits/pays — it
// excludes the workflow, SSE round trip, Cloud Run cold starts, and the
// grounding fee that PRICE_TABLE_TWD above already accounts for.
export const GOOGLE_PRICING_TABLE = [
  { model: "gemini-3.5-flash-lite", input: "$0.30", output: "$2.50" },
  { model: "gemini-3.5-flash", input: "$1.50", output: "$9.00" },
  {
    model: "gemini-2.5-pro",
    input: "$1.25 (≤200K) / $2.50 (>200K)",
    output: "$10.00 (≤200K) / $15.00 (>200K)",
  },
  {
    model: "gemini-3.1-pro-preview",
    input: "$2.00 (≤200K) / $4.00 (>200K)",
    output: "$12.00 (≤200K) / $18.00 (>200K)",
  },
];
// OpenAI's official per-1M-token pricing (short context), looked up 2026-09-12
// — from Ref/phase2a_ui_copy_2026_09_12.md §4. Tell the backend if this changes
// so both sides update together. Unlike the Gemini rows, search is billed
// per-call here, and a single question can trigger many searches.
// Measured per-query cost/latency for the GPT path — the counterpart to
// PRICE_TABLE_TWD above, from the backend's 27-combination matrix run
// (2026-09-13). Two differences from the Gemini table:
//
//   - There is no thinking column. GPT's `fast` and `balanced` are the same
//     reasoning mode underneath, so they are reported as one "standard" range
//     (lower bound = fast, upper = balanced) matching the two-tier picker.
//   - The deep tier is tracked separately because it failed 6 of 9 runs; only
//     terra produced numbers at all.
//
// Still a single question per cell, not a 72-run statistic. Ranges, not points.
export const GPT_PRICE_TABLE_TWD = [
  {
    model: "gpt-5.6-luna",
    verbosity: "low",
    latency: "34.9–39.8s",
    cost: "NT$1.15–1.55",
  },
  {
    model: "gpt-5.6-luna",
    verbosity: "medium",
    latency: "48.6–68.3s",
    cost: "NT$1.21–2.08",
  },
  {
    model: "gpt-5.6-luna",
    verbosity: "high",
    latency: "71.1–83.8s",
    cost: "NT$1.77–2.04",
  },
  {
    model: "gpt-5.6-terra",
    verbosity: "low",
    latency: "27.7–30.5s",
    cost: "NT$3.97–4.87",
  },
  {
    model: "gpt-5.6-terra",
    verbosity: "medium",
    latency: "43.2–44.2s",
    cost: "NT$4.65–4.67",
  },
  {
    model: "gpt-5.6-terra",
    verbosity: "high",
    latency: "70.0–75.1s",
    cost: "NT$6.01–8.59",
  },
  {
    model: "gpt-5.6-sol",
    verbosity: "low",
    latency: "45.7–104.4s",
    cost: "NT$6.51–13.71",
  },
  {
    model: "gpt-5.6-sol",
    verbosity: "medium",
    latency: "56.7–98.2s",
    cost: "NT$7.07–13.14",
  },
  {
    model: "gpt-5.6-sol",
    verbosity: "high",
    latency: "101.7–180.8s",
    cost: "NT$9.09–16.56",
  },
];

export const OPENAI_PRICING_TABLE = [
  {
    model: "gpt-5.6-sol",
    input: "$4.00",
    output: "$20.00",
    search: "$10.00 / 1,000",
  },
  {
    model: "gpt-5.6-terra",
    input: "$2.00",
    output: "$12.00",
    search: "$10.00 / 1,000",
  },
  {
    model: "gpt-5.6-luna",
    input: "$0.20",
    output: "$1.20",
    search: "$10.00 / 1,000",
  },
];

export const GOOGLE_PRICING_URL =
  "https://cloud.google.com/vertex-ai/generative-ai/pricing";
export const OPENAI_PRICING_URL =
  "https://developers.openai.com/api/docs/pricing";

function ChevronDownIcon({ open }) {
  return (
    <svg
      className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function AlarmClockIcon() {
  return (
    <svg
      className="w-3 h-3 shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="13" r="7" strokeWidth={2} />
      <path strokeLinecap="round" strokeWidth={2} d="M12 10v3.5l2.5 1.5" />
      <path strokeLinecap="round" strokeWidth={2} d="M5 4L3 6M19 4l2 2" />
      <path strokeLinecap="round" strokeWidth={2} d="M9.5 3h5" />
    </svg>
  );
}

// Relative wait indicator, like a price tier ($/$$/$$$) but for latency —
// more clocks means a longer measured wait. Not a guarantee: actual speed
// varies with the question and current API load, see each option's speed text
// for the measured range.
function SpeedClocks({ rating }) {
  return (
    <span className="flex items-center gap-0.5 shrink-0">
      {Array.from({ length: rating }, (_, i) => (
        <AlarmClockIcon key={i} />
      ))}
    </span>
  );
}

// Relative cost tier ($/$$/$$$), not an exact figure — see the "Price list"
// flyout (PRICE_TABLE_TWD) for the real measured numbers per (model, thinking).
function CostTier({ rating }) {
  return (
    <span className="text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums shrink-0">
      {"$".repeat(rating)}
    </span>
  );
}

const TRIGGER_PILL_CLASS =
  "inline-flex items-center gap-1.5 text-xs font-medium text-white bg-orange-500 hover:bg-orange-200 dark:hover:bg-orange-900/50 px-3 py-1.5 rounded-full border border-orange-200/60 dark:border-orange-800/60 transition-colors";

// Sub-panels (content length, thinking) still open upward next to their row.
// The MAIN panel does not: anchored to the trigger it ran off-screen either
// way, because the input box it sits beside can be near the top (empty state)
// or at the bottom (conversation), and six models made it tall enough to
// overflow both. It is a centered overlay instead — see ChatSettingsPicker.
// No overflow/max-height here: the flyout submenus (Content length,
// Thinking) are absolutely positioned outside this panel's own box, and
// overflow-y-auto on this element would clip them (CSS forces overflow-x to
// at least auto whenever overflow-y isn't visible). The scrollable list
// itself gets its own inner max-height/overflow instead.
function ForwardChevronIcon() {
  return (
    <svg
      className="w-3.5 h-3.5 shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 18l6-6-6-6"
      />
    </svg>
  );
}

const THINKING_LABEL = {
  fast: { en: "Fast", zh: "快速" },
  balanced: { en: "Balanced", zh: "均衡" },
  deep: { en: "Deep", zh: "深入" },
};

// Full measured price/latency table, single scrolling page: PIMS_FAQ's
// measured numbers first (what the user actually waits/pays), Google's
// official per-token pricing below for reference. Centered modal, not a
// flyout — this is reference material to read, not a quick pick.
function PriceListModal({
  language,
  onClose,
  priceNote,
  modelOptions,
  showBillingRates,
}) {
  const hasGpt = modelOptions.some((o) => o.provider === "gpt");
  const [tab, setTab] = useState("gemini");
  const tabs = [
    { id: "gemini", label: "Gemini" },
    ...(hasGpt ? [{ id: "gpt", label: "GPT" }] : []),
    ...(showBillingRates
      ? [{ id: "billing", label: language === "zh" ? "計費單價" : "Billing rates" }]
      : []),
  ];
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-6xl max-h-[85vh] overflow-y-auto overscroll-contain bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
            {language === "zh" ? "價目表" : "Price list"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none px-1"
            aria-label={language === "zh" ? "關閉" : "Close"}
          >
            ×
          </button>
        </div>

        <div className="p-4 flex flex-col gap-5">
          {/* Two tabs rather than one long page: the Gemini figures come from
              72 measured requests and bill purely by token, while the GPT ones
              are single runs that bill per web search. Reading them as one
              list invites comparing numbers that were not measured the same
              way. The GPT tab only exists for tabs that offer GPT, and the
              Billing rates tab only where usage is billed (TG PIMS Agent). */}
          {tabs.length > 1 && (
            <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 -mt-1">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                    tab === t.id
                      ? "border-orange-500 text-orange-600 dark:text-orange-400"
                      : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {tab === "gemini" && (
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {language === "zh"
                    ? "實測數據 · 每次查詢"
                    : "Measured · per query"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {language === "zh"
                    ? "72 次真實請求量測（含流程、SSE、Cloud Run 冷啟動），2026-08-31，匯率 NT$31/USD。"
                    : "72 requests against the deployed service (includes workflow, SSE, Cloud Run cold starts), measured 2026-08-31, NT$31/USD."}
                </p>
                {/* Callers whose numbers were measured against a different service
                  say so here, rather than letting these read as their own. */}
                {priceNote && (
                  <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
                    {priceNote}
                  </p>
                )}
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-base border-collapse tabular-nums">
                    <thead>
                      <tr className="text-left text-gray-400 dark:text-gray-500">
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "模型" : "Model"}
                        </th>
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "思考深度" : "Thinking"}
                        </th>
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh"
                            ? "延遲（中位數）"
                            : "Latency (median)"}
                        </th>
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "Token 成本" : "Token cost"}
                        </th>
                        <th className="py-1.5 font-medium">
                          {language === "zh" ? "搜尋費用" : "Grounding fee"}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      {/* Filtered, not deleted from the source table: the measured
                        rows for retired models stay on record (they cost 72 real
                        requests to produce) but showing a model the picker cannot
                        select would just confuse. Filtering before the index is
                        taken keeps the i % 3 row grouping correct. */}
                      {PRICE_TABLE_TWD.filter((row) =>
                        modelOptions.some((o) => o.id === row.model),
                      ).map((row, i) => (
                        <tr
                          key={`${row.model}-${row.thinking}`}
                          className={
                            i % 3 === 0
                              ? "border-t border-gray-100 dark:border-gray-700"
                              : ""
                          }
                        >
                          <td className="py-1.5 pr-4 font-mono whitespace-nowrap">
                            {i % 3 === 0 ? row.model : ""}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {THINKING_LABEL[row.thinking][language]}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {row.latency}{" "}
                            <span className="text-gray-400 dark:text-gray-500">
                              ({row.latencyMedian})
                            </span>
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {row.tokenCost}
                          </td>
                          <td className="py-1.5 whitespace-nowrap">
                            {row.groundingFee}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-sm text-gray-400 dark:text-gray-500 mt-2 flex flex-col gap-1">
                  <p>
                    {language === "zh"
                      ? "搜尋費用怎麼算：gemini-2.5-pro 按「Grounding Prompt」（一次提問）計費，$35 / 1,000 次；其餘三檔（3.x 系列）按「Grounding Query」（一次實際查詢，一次提問可能觸發多次）計費，$14 / 1,000 次。換算 NT$31/USD 後即為 NT$1.09 與 NT$0.43。"
                      : 'How it\'s calculated: gemini-2.5-pro bills per "Grounding Prompt" (one question) at $35/1,000; the other three (Gemini 3.x family) bill per "Grounding Query" (one actual index lookup — one question can trigger several) at $14/1,000. At NT$31/USD that\'s NT$1.09 and NT$0.43 respectively.'}
                  </p>
                  <p>
                    {language === "zh"
                      ? "在每月免費額度內不計費：gemini-2.5-pro 有自己的每日額度（10,000 次/日）；其餘三檔共用 5,000 次/月的額度（跨模型合併計算，不是各自獨立）。上表數字是超額後的單價，實際會不會產生這筆費用要看當月用量。"
                      : "Free within the monthly quota: gemini-2.5-pro has its own daily allowance (10,000/day); the other three share a single 5,000/month pool across models (not separate per model). The figures above are the over-quota rate — whether this actually costs anything depends on monthly volume."}
                  </p>
                </div>
              </div>

              <div className="h-px bg-gray-100 dark:bg-gray-700" />

              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {language === "zh"
                    ? "Google 官方單價（USD，每 100 萬 tokens）"
                    : "Google's official pricing (USD, per 1M tokens)"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {language === "zh"
                    ? "原始單價，不含流程/SSE/冷啟動與搜尋費用，僅供參考。"
                    : "Raw unit price — excludes workflow/SSE/cold-start time and the grounding fee, for reference only."}
                </p>
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-base border-collapse tabular-nums">
                    <thead>
                      <tr className="text-left text-gray-400 dark:text-gray-500">
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "模型" : "Model"}
                        </th>
                        <th className="py-1.5 pr-4 font-medium">Input (USD)</th>
                        <th className="py-1.5 font-medium">Output (USD)</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      {GOOGLE_PRICING_TABLE.filter((row) =>
                        modelOptions.some((o) => o.id === row.model),
                      ).map((row) => (
                        <tr
                          key={row.model}
                          className="border-t border-gray-100 dark:border-gray-700"
                        >
                          <td className="py-1.5 pr-4 font-mono whitespace-nowrap">
                            {row.model}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {row.input}
                          </td>
                          <td className="py-1.5 whitespace-nowrap">
                            {row.output}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <a
                  href={GOOGLE_PRICING_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-sm text-orange-600 dark:text-orange-400 hover:underline mt-2"
                >
                  {language === "zh"
                    ? "查看 Google Vertex AI 官方定價頁面 ↗"
                    : "View Google Vertex AI's official pricing page ↗"}
                </a>
              </div>
            </div>
          )}

          {hasGpt && tab === "gpt" && (
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {language === "zh"
                    ? "實測數據 · 每次查詢"
                    : "Measured · per query"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {language === "zh"
                    ? "27 組合實測（3 模型 × 3 思考檔位 × 3 長度），2026-09-13，匯率 NT$31/USD。區間下界為較省力的一檔、上界為預設檔。"
                    : "27 combinations measured (3 models x 3 thinking tiers x 3 lengths), 2026-09-13, NT$31/USD. Range spans the two standard tiers."}
                </p>
                {/* Says why the Deep tier is missing from two of the three
                    models, so its absence reads as a decision rather than an
                    oversight. The two reasons are different and both are
                    stated: luna cannot do it, sol is not being paid for yet. */}
                <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
                  {language === "zh"
                    ? "「深入」目前只開放 gpt-5.6-terra。gpt-5.6-sol 基於 token 成本考量暫不開放（仍在評估中）；gpt-5.6-luna 則是 OpenAI 原生不支援深入思考模式。"
                    : "The Deep tier is currently offered on gpt-5.6-terra only. It is withheld on gpt-5.6-sol on token-cost grounds while we are still evaluating it, and gpt-5.6-luna has no deep reasoning mode at OpenAI's end."}
                </p>
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-base border-collapse tabular-nums">
                    <thead>
                      <tr className="text-left text-gray-400 dark:text-gray-500">
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "模型" : "Model"}
                        </th>
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "回答長度" : "Length"}
                        </th>
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "延遲" : "Latency"}
                        </th>
                        <th className="py-1.5 font-medium">
                          {language === "zh" ? "單次成本" : "Cost per query"}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      {GPT_PRICE_TABLE_TWD.map((row, i) => (
                        <tr
                          key={`${row.model}-${row.verbosity}`}
                          className={
                            i % 3 === 0
                              ? "border-t border-gray-100 dark:border-gray-700"
                              : ""
                          }
                        >
                          <td className="py-1.5 pr-4 font-mono whitespace-nowrap">
                            {i % 3 === 0 ? row.model : ""}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {VERBOSITY_OPTIONS.find(
                              (o) => o.id === row.verbosity,
                            )?.label[language] ?? row.verbosity}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {row.latency}
                          </td>
                          <td className="py-1.5 whitespace-nowrap">
                            {row.cost}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {language === "zh"
                    ? "OpenAI 官方單價（USD，每 100 萬 tokens）"
                    : "OpenAI's official pricing (USD, per 1M tokens)"}
                </h3>
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-base border-collapse tabular-nums">
                    <thead>
                      <tr className="text-left text-gray-400 dark:text-gray-500">
                        <th className="py-1.5 pr-4 font-medium">
                          {language === "zh" ? "模型" : "Model"}
                        </th>
                        <th className="py-1.5 pr-4 font-medium">Input (USD)</th>
                        <th className="py-1.5 pr-4 font-medium">
                          Output (USD)
                        </th>
                        <th className="py-1.5 font-medium">
                          {language === "zh" ? "搜尋" : "Search"}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-gray-200">
                      {OPENAI_PRICING_TABLE.filter((row) =>
                        modelOptions.some((o) => o.id === row.model),
                      ).map((row) => (
                        <tr
                          key={row.model}
                          className="border-t border-gray-100 dark:border-gray-700"
                        >
                          <td className="py-1.5 pr-4 font-mono whitespace-nowrap">
                            {row.model}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {row.input}
                          </td>
                          <td className="py-1.5 pr-4 whitespace-nowrap">
                            {row.output}
                          </td>
                          <td className="py-1.5 whitespace-nowrap">
                            {row.search}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {language === "zh"
                    ? "定價查詢日期：2026-09-12"
                    : "Pricing looked up 2026-09-12"}
                </p>
                <a
                  href={OPENAI_PRICING_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-sm text-orange-600 dark:text-orange-400 hover:underline mt-1"
                >
                  {language === "zh"
                    ? "查看 OpenAI 官方定價頁面 ↗"
                    : "View OpenAI's official pricing page ↗"}
                </a>
              </div>
            </div>
          )}

          {showBillingRates && tab === "billing" && (
            <BillingRatesTab language={language} />
          )}
        </div>
      </div>
    </div>
  );
}

// "200K", "272K" - how the providers write a long-context size.
const formatTokenCount = (n) =>
  n >= 1000 && n % 1000 === 0 ? `${n / 1000}K` : n.toLocaleString();

// The rates usage billing actually charges (GET /api/v2/usage-billing/prices,
// the newest price version in effect), as opposed to the measured per-query
// figures in the other two tabs. Loaded when the tab is opened.
function BillingRatesTab({ language }) {
  const zh = language === "zh";
  const { currentUser } = useAuth();
  const [prices, setPrices] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `${getApiUrl()}/api/v2/usage-billing/prices`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.error || `Request failed (${response.status})`);
        }
        if (!cancelled) setPrices(data);
      } catch (err) {
        console.error("Failed to load billing prices:", err);
        if (!cancelled) setError(err.message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  if (error) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        {zh ? "無法載入計費單價：" : "Couldn't load the billing rates: "}
        {error}
      </p>
    );
  }
  if (!prices) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-6 h-6 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (!prices.price_version) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {zh ? "尚未載入計費價目表。" : "No billing price list is loaded yet."}
      </p>
    );
  }

  const fees = (row) =>
    [
      row.web_search_call_usd > 0 &&
        (zh
          ? `網路搜尋 ${formatUsd(row.web_search_call_usd)} / 次`
          : `Web search ${formatUsd(row.web_search_call_usd)} / call`),
      row.grounding_query_usd > 0 &&
        (zh
          ? `搜尋接地 ${formatUsd(row.grounding_query_usd)} / 查詢`
          : `Search grounding ${formatUsd(row.grounding_query_usd)} / query`),
      row.grounded_prompt_usd > 0 &&
        (zh
          ? `搜尋接地 ${formatUsd(row.grounded_prompt_usd)} / 提問`
          : `Search grounding ${formatUsd(row.grounded_prompt_usd)} / prompt`),
    ].filter(Boolean);
  const providers = [
    { id: "vertex_ai", label: "Gemini" },
    { id: "openai", label: "GPT" },
  ];
  const effective = prices.effective_from
    ? new Date(prices.effective_from).toLocaleDateString(
        zh ? "zh-TW" : "en-GB",
        { day: "numeric", month: "short", year: "numeric" },
      )
    : null;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          {zh
            ? "實際計費單價（USD，每 100 萬 tokens）"
            : "What your usage is charged at (USD, per 1M tokens)"}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {zh
            ? `價目版本 ${prices.price_version}${effective ? `，自 ${effective} 起生效` : ""}。這些是計入您每週額度的單價：思考 token 以輸出計價，快取輸入是供應商從快取重複使用的部分；單次呼叫的輸入超過長上下文門檻時，該次呼叫全部 token 以長上下文單價計算。`
            : `Price version ${prices.price_version}${effective ? `, in effect since ${effective}` : ""}. These are the rates counted against your weekly quota. Thinking tokens are charged as output; cached input is the part of the input the provider reused from its cache. When one call's input is above the long-context size, every token of that call is charged at the long-context rate.`}
        </p>
      </div>
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-base border-collapse tabular-nums">
          <thead>
            <tr className="text-left text-gray-400 dark:text-gray-500">
              <th className="py-1.5 pr-4 font-medium">{zh ? "模型" : "Model"}</th>
              <th className="py-1.5 pr-4 font-medium">{zh ? "端點" : "Endpoint"}</th>
              <th className="py-1.5 pr-4 font-medium">{zh ? "輸入" : "Input"}</th>
              <th className="py-1.5 pr-4 font-medium">
                {zh ? "快取輸入" : "Cached input"}
              </th>
              <th className="py-1.5 pr-4 font-medium">{zh ? "輸出" : "Output"}</th>
              <th className="py-1.5 pr-4 font-medium">
                {zh ? "長上下文（輸入 / 快取 / 輸出）" : "Long context (input / cached / output)"}
              </th>
              <th className="py-1.5 font-medium">{zh ? "按次計費" : "Per use"}</th>
            </tr>
          </thead>
          {providers.map((provider) => {
            const rows = prices.prices.filter((p) => p.provider === provider.id);
            if (rows.length === 0) return null;
            return (
              <tbody
                key={provider.id}
                className="text-gray-700 dark:text-gray-200"
              >
                <tr>
                  <td
                    colSpan={7}
                    className="pt-3 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                  >
                    {provider.label}
                  </td>
                </tr>
                {rows.map((row) => (
                  <tr
                    key={`${row.model}-${row.endpoint}`}
                    className="border-t border-gray-100 dark:border-gray-700"
                  >
                    <td className="py-1.5 pr-4 font-mono whitespace-nowrap">
                      {row.model}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {row.endpoint === "any" ? "—" : row.endpoint}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {formatUsd(row.input_per_1m)}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {formatUsd(row.cached_input_per_1m)}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {formatUsd(row.output_per_1m)}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap">
                      {row.long_context_above_tokens && row.long_input_per_1m !== null
                        ? `> ${formatTokenCount(row.long_context_above_tokens)}: ${formatUsd(row.long_input_per_1m)} / ${formatUsd(row.long_cached_input_per_1m)} / ${formatUsd(row.long_output_per_1m)}`
                        : "—"}
                    </td>
                    <td className="py-1.5 text-sm whitespace-nowrap">
                      {fees(row).length > 0
                        ? fees(row).map((fee) => <div key={fee}>{fee}</div>)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      </div>
    </div>
  );
}

// Single trigger pill (shows the current model) that opens one panel: the
// model list, with a "Content length" summary row pinned below it. Tapping
// that row opens a second, independent flyout panel to the side — the model
// panel stays open and visible, it isn't replaced — mirroring a nested
// submenu rather than swapping views in place.
export function ChatSettingsPicker({
  verbosity,
  onVerbosityChange,
  thinking,
  onThinkingChange,
  groundingModel,
  onGroundingModelChange,
  language,
  // Shown at the top of the price flyout. Pass it when these measurements came
  // from a different service than the one this picker is driving.
  priceNote,
  // Which models this tab's backend accepts. Defaults to Gemini-only so the FAQ
  // tab, whose service knows no other ids, stays correct without passing it.
  modelOptions = GROUNDING_MODEL_OPTIONS,
  triggerClassName = TRIGGER_PILL_CLASS,
  // Adds the "Billing rates" tab to the price list - live from usage billing.
  // Only TG PIMS Agent passes it: the FAQ tab's chats aren't billed.
  showBillingRates = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVerbosityOpen, setIsVerbosityOpen] = useState(false);
  const [isThinkingOpen, setIsThinkingOpen] = useState(false);
  const [isPriceListOpen, setIsPriceListOpen] = useState(false);

  const selectedModel =
    modelOptions.find((o) => o.id === groundingModel) ??
    modelOptions.find((o) => o.isDefault) ??
    modelOptions[0];
  const selectedVerbosity =
    VERBOSITY_OPTIONS.find((o) => o.id === verbosity) ?? VERBOSITY_OPTIONS[1];
  // GPT and Gemini disagree on what the thinking axis even is, so the list
  // follows the selected model. Switching from Gemini's `fast` to a GPT model
  // leaves a value with no entry in GPT's list; `selectedThinking` falls back to
  // Standard so the row never reads blank, and onGroundingModelChange below
  // rewrites the value so what is displayed is what gets sent.
  const isGptSelected = selectedModel?.provider === "gpt";
  // Models that cannot do the deep tier never show it — offering it would be an
  // option that always fails rather than one that is merely slow.
  const thinkingOptions = isGptSelected
    ? GPT_THINKING_OPTIONS.filter(
        (o) => !(o.id === "deep" && selectedModel?.noDeepTier),
      )
    : THINKING_OPTIONS;
  const selectedThinking =
    thinkingOptions.find((o) => o.id === thinking) ?? thinkingOptions[0];

  // Picking an option only updates that setting and collapses its own section —
  // the dialog stays open so several settings can be adjusted in a row. It
  // closes via the trigger, the × button, or the backdrop.

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={triggerClassName}
      >
        <span>{language === "zh" ? "模型" : "Model"}:</span>
        <span className="font-mono">{selectedModel.label}</span>
        <ChevronDownIcon open={isOpen} />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/40"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto overscroll-contain bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {language === "zh" ? "回答設定" : "Answer settings"}
              </span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none px-1"
                aria-label={language === "zh" ? "關閉" : "Close"}
              >
                ×
              </button>
            </div>
            <div className="rounded-t-xl">
              {modelOptions.map((option) => {
                const isSelected = option.id === groundingModel;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      onGroundingModelChange(option.id);
                      // Keep thinking valid for the model being switched to.
                      // Gemini's `fast` has no entry in GPT's two-tier list, and
                      // `deep` has none on a model without a pro mode — leaving
                      // either would show one tier while sending another.
                      const nextOptions =
                        option.provider === "gpt"
                          ? GPT_THINKING_OPTIONS.filter(
                              (o) => !(o.id === "deep" && option.noDeepTier),
                            )
                          : THINKING_OPTIONS;
                      if (!nextOptions.some((o) => o.id === thinking)) {
                        onThinkingChange("balanced");
                      }
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors ${
                      isSelected ? "bg-orange-100 dark:bg-orange-900/30" : ""
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${
                          isSelected
                            ? "bg-orange-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-semibold text-sm text-gray-800 dark:text-gray-100">
                            {option.label}
                          </span>
                          {option.isDefault && (
                            <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-full">
                              {language === "zh" ? "預設" : "Default"}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <SpeedClocks rating={option.speedRating} />
                            {option.speed[language]}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">
                            ·
                          </span>
                          <CostTier rating={option.costRating} />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {option.depth[language]}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="h-px bg-gray-100 dark:bg-gray-700" />

            <div className="relative">
              <button
                type="button"
                onClick={() => setIsVerbosityOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                <span className="font-medium">
                  {language === "zh" ? "內容長度" : "Content length"}
                </span>
                <span className="flex items-center gap-1 text-gray-400 dark:text-gray-500">
                  <span className="text-gray-700 dark:text-gray-200 font-medium">
                    {selectedVerbosity.label[language]}
                  </span>
                  <ForwardChevronIcon />
                </span>
              </button>

              {isVerbosityOpen && (
                <div className="mx-3 mb-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {VERBOSITY_OPTIONS.map((option) => {
                    const isSelected = option.id === verbosity;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          onVerbosityChange(option.id);
                          setIsVerbosityOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors ${
                          isSelected
                            ? "bg-orange-100 dark:bg-orange-900/30"
                            : ""
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              isSelected
                                ? "bg-orange-500"
                                : "bg-gray-300 dark:bg-gray-600"
                            }`}
                          />
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-gray-800 dark:text-gray-100">
                              {option.label[language]}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {option.description[language]}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setIsThinkingOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                <span className="font-medium">
                  {language === "zh" ? "思考深度" : "Thinking"}
                </span>
                <span className="flex items-center gap-1 text-gray-400 dark:text-gray-500">
                  <span className="text-gray-700 dark:text-gray-200 font-medium">
                    {selectedThinking.label[language]}
                  </span>
                  <ForwardChevronIcon />
                </span>
              </button>

              {isThinkingOpen && (
                <div className="mx-3 mb-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {thinkingOptions.map((option) => {
                    const isSelected = option.id === thinking;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          onThinkingChange(option.id);
                          setIsThinkingOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors ${
                          isSelected
                            ? "bg-orange-100 dark:bg-orange-900/30"
                            : ""
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              isSelected
                                ? "bg-orange-500"
                                : "bg-gray-300 dark:bg-gray-600"
                            }`}
                          />
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-gray-800 dark:text-gray-100">
                              {option.label[language]}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {option.description[language]}
                            </div>
                            {/* Carried by tiers with a known failure rate, so
                                the cost of picking one is visible at the point
                                of choosing rather than after a timeout. */}
                            {option.warn && (
                              <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                                {option.warn[language]}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsPriceListOpen(true)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
            >
              <span className="font-medium">
                {language === "zh" ? "價目表" : "Price list"}
              </span>
              <ForwardChevronIcon />
            </button>
          </div>
        </div>
      )}

      {isPriceListOpen && (
        <PriceListModal
          language={language}
          priceNote={priceNote}
          modelOptions={modelOptions}
          showBillingRates={showBillingRates}
          onClose={() => setIsPriceListOpen(false)}
        />
      )}
    </div>
  );
}
